/**
 * Pure utility functions for deriving Human data from UserProfile1.
 *
 * These functions have no React, Redux, or Firebase dependencies.
 * They are used by the ensureCurrentUserHuman thunk in map3PendingSlice
 * and are extracted here for testability.
 */

import type { UserProfile1 } from '@mm-shared/types/UserProfile1';
import type { Human } from '@mm-shared/types/Human';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';

/**
 * Derive a display name from a UserProfile1.
 *
 * Priority:
 *   1. userName (if non-empty)
 *   2. firstName + lastName (joined, trimmed)
 *   3. First email address
 *   4. "Unknown" as last resort
 */
export function deriveNameFromProfile(profile: UserProfile1): string {
  if (profile.userName && profile.userName.trim()) {
    return profile.userName.trim();
  }

  const first = profile.firstName?.trim() ?? '';
  const last = profile.lastName?.trim() ?? '';
  const fullName = `${first} ${last}`.trim();
  if (fullName) {
    return fullName;
  }

  if (profile.email && profile.email.length > 0 && profile.email[0]) {
    return profile.email[0];
  }

  return 'Unknown';
}

/**
 * Build a complete Human object from a UserProfile1.
 *
 * Generates a new uuid for the Human doc, links it to the user via uid,
 * and derives the display name from the profile fields.
 */
export function buildHumanFromUserProfile(profile: UserProfile1): Human {
  const uuid = crypto.randomUUID();
  return {
    uuid,
    shortId: getShortIdFromUuid(uuid),
    name: deriveNameFromProfile(profile),
    email: profile.email?.[0] ?? undefined,
    uid: profile.uid,
    version: 1,
    createdDate: new Date().toISOString(),
    createdBy: profile.uid,
  };
}
